import { Plus, X } from 'lucide-react';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { cn } from '@talentmesh/ui/lib/utils';
import { Badge } from './badge';
import {
    Combobox,
    ComboboxContent,
    ComboboxFieldInput,
    ComboboxGroup,
    ComboboxItem,
    ComboboxLabel,
    ComboboxList,
} from './combobox';
import { Spinner } from './spinner';

export interface AutocompleteOption {
    id?: string;
    label: string;
    /** When set on any option, the dropdown renders group headers (e.g. cities / regions). */
    group?: string;
}

export interface AutocompleteChipsProps {
    name: string;
    placeholder?: string;
    disabled?: boolean;
    limit?: number;
    disableFreeSolo?: boolean;
    getAutocompleteOptions: (search: string) => Promise<AutocompleteOption[]>;
    className?: string;
    /**
     * When set, the default append({ id, label }) is skipped; use this to append custom row shapes
     * (e.g. geocoded locations). `append` is the field-array append for `name`.
     */
    onOptionSelect?: (option: AutocompleteOption, append: (value: unknown) => void) => void | Promise<void>;
    /** Chip text when stored values are not plain `{ label }` (e.g. geocode rows). */
    getChipLabel?: (chip: unknown) => string;
    /** Async work after picking an option (e.g. geocoding). */
    pending?: boolean;
    /** Optional `data-cy` on the root. */
    dataCy?: string;
}

function AutocompleteChips({
    name,
    placeholder = 'Add item...',
    disabled = false,
    limit = 10,
    disableFreeSolo = false,
    getAutocompleteOptions,
    className,
    onOptionSelect,
    getChipLabel,
    pending = false,
    dataCy,
}: AutocompleteChipsProps) {
    const { control, trigger } = useFormContext();
    const { fields, append, remove } = useFieldArray({ control, name });
    const chips = fields as (AutocompleteOption & { id: string })[];

    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<AutocompleteOption[]>([]);
    const [comboboxOpen, setComboboxOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pickNonce, setPickNonce] = useState(0);
    const [highlightedValue, setHighlightedValue] = useState<AutocompleteOption | undefined>();

    const inputRef = useRef<HTMLInputElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
    const [dropDirection, setDropDirection] = useState<'bottom' | 'top'>('bottom');

    const isAtLimit = chips.length >= limit;

    const chipLabel = useCallback(
        (chip: (typeof chips)[number]) => (getChipLabel ? getChipLabel(chip) : (chip as AutocompleteOption).label),
        [getChipLabel],
    );

    useEffect(() => {
        trigger(name);
    }, [disabled, name, trigger]);

    useEffect(() => {
        if (disableFreeSolo) {
            let cancelled = false;
            const fetchAll = async () => {
                setLoading(true);
                const all = await getAutocompleteOptions('');
                if (!cancelled) {
                    setOptions(all);
                    setLoading(false);
                }
            };
            fetchAll();
            return () => {
                cancelled = true;
            };
        }
        return undefined;
    }, [disableFreeSolo, getAutocompleteOptions]);

    const fetchOptions = useCallback(
        async (search: string) => {
            if (disableFreeSolo && search === '') {
                const all = await getAutocompleteOptions('');
                setOptions(all);
                setLoading(false);
                return;
            }

            if (!search) {
                if (!disableFreeSolo) setOptions([]);
                return;
            }

            setLoading(true);
            let results = await getAutocompleteOptions(search);

            results = results.filter((opt) => !chips.some((chip) => chipLabel(chip) === opt.label));

            if (!results.length && !disableFreeSolo) {
                setOptions([{ id: undefined, label: search }]);
            } else {
                setOptions(results);
            }
            setLoading(false);
        },
        [disableFreeSolo, chips, chipLabel, getAutocompleteOptions],
    );

    const hasGroupedOptions = options.some((o) => Boolean(o.group));
    const groupedOptions = React.useMemo(() => {
        if (!hasGroupedOptions) return null;
        return options.reduce<Record<string, AutocompleteOption[]>>((acc, opt) => {
            const g = opt.group ?? '';
            if (!acc[g]) acc[g] = [];
            acc[g].push(opt);
            return acc;
        }, {});
    }, [options, hasGroupedOptions]);

    const showList = comboboxOpen && (loading || options.length > 0);

    const [portalRoot, setPortalRoot] = useState<HTMLDivElement | null>(null);
    const setContainerRef = useCallback((node: HTMLDivElement | null) => {
        setPortalRoot(node);
    }, []);

    const itemsForRoot = useMemo(() => {
        if (!hasGroupedOptions || !groupedOptions) return options;
        return Object.entries(groupedOptions).map(([groupName, groupOpts]) => ({
            id: groupName,
            label: groupName,
            items: groupOpts,
        }));
    }, [hasGroupedOptions, groupedOptions, options]);

    /** Invisible mirror text so the input column width tracks value / placeholder (non-breaking spaces preserve width). */
    const sizerText = useMemo(() => {
        const raw = inputValue || placeholder;
        const spaced = raw.replace(/\s/g, '\u00a0');
        return spaced.length > 0 ? spaced : '\u00a0';
    }, [inputValue, placeholder]);

    const addChip = useCallback(
        async (option: AutocompleteOption) => {
            if (isAtLimit || disabled) return;
            if (!onOptionSelect) {
                const isDuplicate = chips.some((chip) => chipLabel(chip) === option.label);
                if (isDuplicate) return;
                append({ id: option.id, label: option.label });
            } else {
                await onOptionSelect(option, append);
            }
            setInputValue('');
            setHighlightedValue(undefined);

            if (disableFreeSolo) {
                const all = await getAutocompleteOptions('');
                setOptions(all);
            } else {
                setOptions([]);
            }

            setPickNonce((n) => n + 1);
            inputRef.current?.focus();
        },
        [isAtLimit, disabled, chips, append, disableFreeSolo, getAutocompleteOptions, onOptionSelect, chipLabel],
    );

    const handleInputValueChange = useCallback(
        (val: string) => {
            setInputValue(val);
            setHighlightedValue(undefined);
            if (!comboboxOpen) setComboboxOpen(true);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                fetchOptions(val);
            }, 200);
        },
        [comboboxOpen, fetchOptions],
    );

    const onComboboxOpenChange = useCallback((open: boolean) => {
        if (!open) {
            setComboboxOpen(false);
            setHighlightedValue(undefined);
        }
    }, []);

    useEffect(() => {
        if (!showList || !anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const dropdownMaxHeight = 240 + 8;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        setDropDirection(spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }, [showList]);

    const listBody = useMemo(
        () =>
            loading && options.length === 0 ? (
                <div className="text-muted-foreground flex items-center justify-center gap-2 py-4 text-sm">
                    <Spinner className="h-4 w-4" />
                    Loading...
                </div>
            ) : hasGroupedOptions && groupedOptions ? (
                Object.entries(groupedOptions).map(([group, groupOpts]) => (
                    <ComboboxGroup key={group}>
                        <ComboboxLabel>{group}</ComboboxLabel>
                        {groupOpts.map((option) => {
                            const value = option.id ?? `${group}-${option.label}`;
                            return (
                                <ComboboxItem key={value} value={option} className="cursor-pointer">
                                    {option.label}
                                </ComboboxItem>
                            );
                        })}
                    </ComboboxGroup>
                ))
            ) : (
                options.map((option) => (
                    <ComboboxItem key={String(option.id ?? option.label)} value={option} className="cursor-pointer">
                        {option.label}
                    </ComboboxItem>
                ))
            ),
        [loading, options, hasGroupedOptions, groupedOptions],
    );

    const chipElements = useMemo(
        () =>
            chips.map((chip, index) => {
                const label = chipLabel(chip);
                return (
                    <Badge
                        key={chip.id}
                        variant={disabled ? 'default-disabled' : 'default'}
                        className="h-8 max-w-full gap-1.5 rounded-md px-3 py-1 text-sm font-normal"
                    >
                        <span data-testid={`${label}-chip`} className="w-fit truncate">
                            {label}
                        </span>
                        <button
                            data-testid="CancelIcon"
                            disabled={disabled}
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                remove(index);
                            }}
                            className="ml-0.5 shrink-0 rounded-full hover:opacity-80 focus:outline-none"
                            aria-label={`Remove ${label}`}
                        >
                            <X
                                className={cn(
                                    'size-4 rounded-full bg-white p-[2px]',
                                    disabled ? 'text-muted-foreground/20' : 'text-primary',
                                )}
                            />
                        </button>
                    </Badge>
                );
            }),
        [chips, disabled, remove, chipLabel],
    );

    const field = (
        <div
            ref={setContainerRef}
            className={cn('relative flex flex-wrap items-center gap-2', className)}
            data-cy={dataCy}
        >
            {chipElements}

            {!isAtLimit && (
                <>
                    <div
                        ref={anchorRef}
                        className={cn(
                            'border-input bg-background ring-offset-background flex h-8 max-w-full items-center gap-1.5 rounded-md border px-3 text-sm transition-colors',
                            'focus-within:ring-primary focus-within:ring-2',
                            disabled && 'pointer-events-none cursor-not-allowed opacity-50',
                        )}
                        onClick={() => {
                            if (!disabled) inputRef.current?.focus();
                        }}
                    >
                        <div className="relative inline-grid max-w-full min-w-10 grid-cols-1 items-center">
                            <span
                                aria-hidden
                                className="invisible col-start-1 row-start-1 max-w-full px-0 py-0 font-sans text-sm leading-none whitespace-pre"
                            >
                                {sizerText}
                            </span>
                            <ComboboxFieldInput
                                ref={inputRef}
                                type="text"
                                placeholder={placeholder}
                                disabled={disabled || pending}
                                className="col-start-1 row-start-1 m-0 flex h-full w-full max-w-full min-w-0 flex-none"
                                autoComplete="off"
                                onFocus={() => {
                                    setComboboxOpen(true);
                                    if (disableFreeSolo && options.length === 0) {
                                        fetchOptions('');
                                    }
                                }}
                                onClick={() => {
                                    setComboboxOpen(true);
                                }}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === 'Enter') {
                                        if (
                                            !disableFreeSolo &&
                                            !onOptionSelect &&
                                            inputValue.trim() &&
                                            highlightedValue === undefined
                                        ) {
                                            e.preventDefault();
                                            addChip({ label: inputValue.trim() });
                                        }
                                        return;
                                    }
                                }}
                            />
                        </div>
                        {pending ? (
                            <Spinner className="text-muted-foreground h-4 w-4 shrink-0 self-center" />
                        ) : (
                            <Plus className="text-muted-foreground h-4 w-4 shrink-0" />
                        )}
                    </div>

                    <ComboboxContent
                        container={portalRoot ?? undefined}
                        anchor={anchorRef}
                        side={dropDirection === 'bottom' ? 'bottom' : 'top'}
                        sideOffset={4}
                        align="start"
                        className={cn(
                            'border-border bg-popover shadow-soft-md max-h-60 min-w-65 border p-0',
                            dropDirection === 'bottom' ? 'mt-1' : 'mb-1',
                        )}
                    >
                        <div id={`${name}-listbox`}>
                            <ComboboxList className="max-h-60">{listBody}</ComboboxList>
                        </div>
                    </ComboboxContent>
                </>
            )}
        </div>
    );

    if (isAtLimit) {
        return field;
    }

    return (
        <Combobox
            key={pickNonce}
            modal={false}
            items={itemsForRoot}
            filter={null}
            open={showList}
            onOpenChange={onComboboxOpenChange}
            inputValue={inputValue}
            onInputValueChange={(value, eventDetails) => {
                if (eventDetails.reason === 'item-press') {
                    return;
                }
                handleInputValueChange(value);
            }}
            defaultValue={null}
            onValueChange={(v) => {
                if (v != null) addChip(v as AutocompleteOption);
            }}
            onItemHighlighted={(v) => setHighlightedValue(v as AutocompleteOption | undefined)}
            itemToStringLabel={(o) => (o == null ? '' : (o as AutocompleteOption).label)}
            isItemEqualToValue={(a, b) =>
                (a as AutocompleteOption | null)?.label === (b as AutocompleteOption | null)?.label &&
                (a as AutocompleteOption | null)?.id === (b as AutocompleteOption | null)?.id
            }
        >
            {field}
        </Combobox>
    );
}

export { AutocompleteChips };
